import { ShieldAlert } from 'lucide-react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { EmptyState } from '@/components/ui/empty-state';
import { useAuthStore } from '@/stores/auth';

/**
 * Hides a route from users who lack the permission it needs.
 *
 * Renders an explanation rather than redirecting. A silent bounce to the
 * dashboard leaves someone who followed a shared link with no idea what happened
 * and nothing to tell their administrator; naming the missing permission turns a
 * dead end into an actionable support request.
 *
 * A UI convenience only — the endpoint behind the screen authorises independently.
 */
export function RequirePermission({
    permission,
    anyOf,
    children,
}: {
    permission?: string;
    anyOf?: string[];
    children: ReactNode;
}) {
    const { t } = useTranslation('common');
    const canAny = useAuthStore((state) => state.canAny);

    const required = anyOf ?? (permission ? [permission] : []);
    const allowed = required.length === 0 || canAny(...required);

    if (allowed) {
        return <>{children}</>;
    }

    return (
        <EmptyState
            icon={ShieldAlert}
            title={t('state.unauthorised_title')}
            description={t('state.unauthorised_description')}
            detail={required.join(', ')}
        />
    );
}
